import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../onboardingV2.locale';
import {
    Wrap,
    Title,
    Subtitle,
    Chat,
    ChatHead,
    Avatar,
    ChatBody,
    Bubble,
    Line,
    Link,
    Time,
} from './OrderMessagePreview.styles';

const SEPARATOR = '========================';
const EXAMPLE_PHONE = '+573105550123';
const EXAMPLE_ORDER_PATH = '/order-review/2481/90317';

/**
 * Same text the storefront sends when a customer places an order
 * (wa10x-v2-new: src/components/Cart/WhatsAppOrderForm.tsx, `message`), without the optional
 * lines (shipping, coupon, delivery time) a new store doesn't have yet. The storefront only sends it
 * in Spanish, so it isn't translated here either.
 */
export const buildOrderMessageLines = ({
    customer,
    storeName,
    storeUrl,
    productName,
    quantity,
    unitPrice,
    currency,
    locale,
}) => {
    const subtotal = formatMoney(quantity * unitPrice, currency, locale);
    return [
        `Hola, soy ${customer},`,
        '',
        `Link a mi pedido: https://${storeUrl}${EXAMPLE_ORDER_PATH}`,
        '',
        `*Nombre:* ${customer}`,
        `*Celular:* ${EXAMPLE_PHONE}`,
        '',
        SEPARATOR,
        '',
        `quiero hacer este pedido en ${storeName}:`,
        '',
        `- ${quantity} *${productName}* / ${formatMoney(unitPrice, currency, locale)}`,
        '',
        SEPARATOR,
        '',
        `*Subtotal:* ${subtotal}`,
        `TOTAL: *${subtotal}*`,
    ];
};

/** WhatsApp formatting: *bold* → <strong>, URLs styled as links (not clickable). */
const renderLine = (line) => {
    if (line.startsWith('Link a mi pedido: ')) {
        const url = line.slice('Link a mi pedido: '.length);
        return (
            <>
                Link a mi pedido: <Link>{url}</Link>
            </>
        );
    }
    return line.split(/(\*[^*]+\*)/g).map((part, index) =>
        part.startsWith('*') && part.endsWith('*') && part.length > 2 ? (
            <strong key={index}>{part.slice(1, -1)}</strong>
        ) : (
            part
        )
    );
};

/** Step 5 · whatsapp: preview of the order message the merchant will receive. */
const OrderMessagePreview = ({ storeName, storeUrl, productName, quantity, unitPrice, currency, locale }) => {
    const { t } = useTranslation();
    const customer = t('SignUpV2.whatsapp.previewCustomer');
    const lines = buildOrderMessageLines({
        customer,
        storeName,
        storeUrl,
        productName,
        quantity,
        unitPrice,
        currency,
        locale,
    });

    return (
        <Wrap aria-labelledby="order-preview-title">
            <Title id="order-preview-title">
                <i className="ri-whatsapp-line" aria-hidden="true" />
                {t('SignUpV2.whatsapp.previewTitle')}
            </Title>
            <Subtitle>{t('SignUpV2.whatsapp.previewSubtitle')}</Subtitle>
            <Chat>
                <ChatHead>
                    <Avatar aria-hidden="true">{customer.charAt(0)}</Avatar>
                    <div>
                        <strong>{customer}</strong>
                        <small>{t('SignUpV2.whatsapp.previewRole')}</small>
                    </div>
                </ChatHead>
                <ChatBody>
                    <Bubble>
                        {lines.map((line, index) =>
                            line === '' ? (
                                <Line key={index} blank aria-hidden="true" />
                            ) : (
                                <Line key={index}>{renderLine(line)}</Line>
                            )
                        )}
                        <Time>{t('SignUpV2.whatsapp.previewTime')}</Time>
                    </Bubble>
                </ChatBody>
            </Chat>
        </Wrap>
    );
};

export default OrderMessagePreview;
